import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Worker is copied to public/ by the prebuild/prestart script so webpack never
// tries to bundle the .mjs file itself (CRA 5 / webpack 5 limitation).
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

/**
 * Words that appear on nearly every page of a financial document and therefore
 * add noise rather than precision when used as search tokens.
 */
const STOP_WORDS = new Set([
  'and','or','the','of','in','for','a','to','were','was','its','at','by','with','as','an',
  // time / period words
  'three','months','ended','september','june','march','december','january','february',
  'april','may','july','august','october','november',
  'fiscal','quarter','annual','year','period','date','ending',
  // ultra-generic financial words
  'net','per','total','from','share','data','except','less','than','not',
  // magnitude words that appear in footnotes / headers
  'thousands','millions','billions','thousand','million','billion',
]);

/**
 * Convert any search string (verbatim quote or metric key) into tokens.
 * Year numbers (2000-2099) are stripped because they appear on every page.
 * Decimal values like 0.33 are extracted before punctuation stripping so
 * the decimal point doesn't split them into sub-length-3 fragments.
 */
function searchToTokens(text) {
  const normalized = text
    .replace(/_/g, ' ')
    .replace(/^custom_metrics\s+/, '')
    .toLowerCase()
    .replace(/\b20\d{2}\b/g, ' ');   // drop years (2000-2099)

  // Extract numeric tokens (decimals + integers) before punctuation is stripped.
  // Strip trailing commas first (e.g. "31," → "31") so they don't become fake
  // 3-char tokens that match "December 31," in PDF headers.
  // Keep both comma-formatted (96,682,774) and stripped (96682774) forms.
  const numericTokens = [];
  const numRe = /\d[\d,]*(?:\.\d+)?/g;
  let nm;
  while ((nm = numRe.exec(normalized)) !== null) {
    const raw = nm[0].replace(/,+$/, '');   // strip trailing comma(s)
    const stripped = raw.replace(/,/g, '');
    if (raw.length > 2) numericTokens.push(raw);
    if (stripped !== raw && stripped.length > 2) numericTokens.push(stripped);
  }

  const wordTokens = normalized
    .replace(/[$,():%.]/g, ' ')
    .split(/\s+/)
    // Drop purely numeric words — they are already captured (more precisely) above.
    .map((w) => w.replace(/[^a-z]/g, ''))
    .filter((w) => w.length > 2 && !STOP_WORDS.has(w));

  return [...new Set([...wordTokens, ...numericTokens])];
}

/**
 * Score a page's text content against the search tokens.
 * Returns the number of distinct tokens found (higher = better match).
 */
function scorePageText(textItems, tokens) {
  const joined = textItems.map((t) => t.str).join(' ').toLowerCase();
  return tokens.filter((tok) => joined.includes(tok)).length;
}

export default function EarningsPdfViewer({ url, searchKey, searchText, onClose }) {
  const canvasRef = useRef(null);
  const textLayerRef = useRef(null);
  const pdfRef = useRef(null);

  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.3);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [matchPage, setMatchPage] = useState(null);
  const highlightedSpansRef = useRef([]);
  const [highlightCount, setHighlightCount] = useState(0);
  const allHitsRef = useRef([]); // [{page, localIndex}] across entire document
  const [totalHighlights, setTotalHighlights] = useState(0);
  const [globalHighlightIndex, setGlobalHighlightIndex] = useState(-1);
  const pendingLocalHighlightRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const renderTaskRef = useRef(null); // tracks active pdfjs RenderTask for cancellation

  // Editable search string — seeded from the LLM source quote or the metric key
  const defaultSearch = searchText || (searchKey ? searchKey.replace(/^custom_metrics\./, '').replace(/_/g, ' ') : '');
  const [customSearch, setCustomSearch] = useState(defaultSearch);

  // Reset editable search whenever the parent changes the metric being searched
  useEffect(() => {
    const next = searchText || (searchKey ? searchKey.replace(/^custom_metrics\./, '').replace(/_/g, ' ') : '');
    setCustomSearch(next);
  }, [searchText, searchKey]);

  const tokens = searchToTokens(customSearch);

  // Load the PDF document
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCurrentPage(1);
    setMatchPage(null);

    const load = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({ url, withCredentials: false });
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        pdfRef.current = pdf;
        setNumPages(pdf.numPages);

        // Fit to container width on initial load
        const page1 = await pdf.getPage(1);
        const naturalVp = page1.getViewport({ scale: 1.0 });
        const containerWidth = scrollContainerRef.current?.clientWidth ?? 0;
        if (containerWidth > 0) {
          const fitScale = (containerWidth - 28) / naturalVp.width; // 28 = 2×padding
          setScale(Math.max(0.5, Math.min(3, +fitScale.toFixed(2))));
        }

        // Scan all pages: find best match page and build global hit list
        if (tokens.length > 0) {
          let bestScore = 0;
          let bestPage = 1;
          const hits = [];
          for (let p = 1; p <= pdf.numPages; p++) {
            const page = await pdf.getPage(p);
            const content = await page.getTextContent();
            let localHitIdx = 0;
            content.items.forEach((item) => {
              if (!item.str?.trim()) return;
              if (tokens.some((tok) => item.str.toLowerCase().includes(tok))) {
                hits.push({ page: p, localIndex: localHitIdx });
                localHitIdx++;
              }
            });
            const score = scorePageText(content.items, tokens);
            if (score > bestScore) { bestScore = score; bestPage = p; }
          }
          if (!cancelled) {
            allHitsRef.current = hits;
            setTotalHighlights(hits.length);
            setGlobalHighlightIndex(-1);
            setMatchPage(bestPage);
            setCurrentPage(bestPage);
          }
        }

        if (!cancelled) setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load PDF: ' + err.message);
          setLoading(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // Re-scan all pages whenever the search tokens change (user edits the search field).
  // Uses the already-loaded PDF so no network request is needed.
  const tokensKey = tokens.join('\x00');
  useEffect(() => {
    const pdf = pdfRef.current;
    if (!pdf || loading) return;
    if (tokens.length === 0) {
      allHitsRef.current = [];
      setTotalHighlights(0);
      setGlobalHighlightIndex(-1);
      return;
    }
    let cancelled = false;
    const scan = async () => {
      let bestScore = 0; let bestPage = currentPage;
      const hits = [];
      for (let p = 1; p <= pdf.numPages; p++) {
        const page = await pdf.getPage(p);
        const content = await page.getTextContent();
        let localHitIdx = 0;
        content.items.forEach((item) => {
          if (!item.str?.trim()) return;
          if (tokens.some((tok) => item.str.toLowerCase().includes(tok))) {
            hits.push({ page: p, localIndex: localHitIdx });
            localHitIdx++;
          }
        });
        const score = scorePageText(content.items, tokens);
        if (score > bestScore) { bestScore = score; bestPage = p; }
      }
      if (!cancelled) {
        allHitsRef.current = hits;
        setTotalHighlights(hits.length);
        setGlobalHighlightIndex(-1);
        setMatchPage(bestPage);
        setCurrentPage(bestPage);
      }
    };
    scan();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokensKey, loading]);

  // Render the current page whenever page/scale changes
  const renderPage = useCallback(async () => {
    const pdf = pdfRef.current;
    const canvas = canvasRef.current;
    const textLayer = textLayerRef.current;
    if (!pdf || !canvas || !textLayer) return;

    // Cancel any in-flight render before starting a new one
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    const page = await pdf.getPage(currentPage);
    const viewport = page.getViewport({ scale });

    canvas.height = viewport.height;
    canvas.width = viewport.width;

    const ctx = canvas.getContext('2d');
    const task = page.render({ canvasContext: ctx, viewport });
    renderTaskRef.current = task;
    try {
      await task.promise;
    } catch (err) {
      if (err?.name === 'RenderingCancelledException') return; // superseded by newer render
      throw err;
    }
    renderTaskRef.current = null;

    // Build text layer
    textLayer.innerHTML = '';
    textLayer.style.width = `${viewport.width}px`;
    textLayer.style.height = `${viewport.height}px`;

    const content = await page.getTextContent();

    // Render each text item as an absolutely-positioned span
    content.items.forEach((item) => {
      if (!item.str?.trim()) return;
      const tx = pdfjsLib.Util.transform(viewport.transform, item.transform);
      const span = document.createElement('span');
      span.textContent = item.str;

      const fontHeight = Math.sqrt(tx[2] * tx[2] + tx[3] * tx[3]);
      const angle = Math.atan2(tx[1], tx[0]);

      span.style.cssText = [
        `position:absolute`,
        `left:${tx[4]}px`,
        `top:${tx[5] - fontHeight}px`,
        `font-size:${fontHeight}px`,
        `line-height:1`,
        `white-space:pre`,
        `transform-origin:0 100%`,
        angle ? `transform:rotate(${angle}rad)` : '',
        `color:transparent`,
        `cursor:text`,
      ].join(';');

      textLayer.appendChild(span);
    });

    // Highlight spans that contain any search token
    const highlighted = [];
    if (tokens.length > 0) {
      const spans = textLayer.querySelectorAll('span');
      spans.forEach((span) => {
        const lower = span.textContent.toLowerCase();
        if (tokens.some((tok) => lower.includes(tok))) {
          span.style.backgroundColor = 'rgba(255, 220, 0, 0.55)';
          span.style.color = 'transparent';
          span.style.borderRadius = '2px';
          span.dataset.highlighted = '1';
          highlighted.push(span);
        }
      });
    }
    highlightedSpansRef.current = highlighted;
    setHighlightCount(highlighted.length);
  }, [currentPage, scale, tokens]);

  useEffect(() => {
    if (!loading) renderPage();
  }, [loading, renderPage]);

  // After a page change triggered by highlight navigation, activate the pending local span
  useEffect(() => {
    const idx = pendingLocalHighlightRef.current;
    if (idx !== null && highlightedSpansRef.current.length > 0) {
      pendingLocalHighlightRef.current = null;
      activateLocalSpan(idx);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightCount]);

  const goTo = (p) => {
    setGlobalHighlightIndex(-1);
    setCurrentPage(Math.max(1, Math.min(numPages, p)));
  };

  const activateLocalSpan = (localIdx) => {
    const spans = highlightedSpansRef.current;
    spans.forEach((s, i) => {
      s.style.backgroundColor = i === localIdx ? 'rgba(255, 140, 0, 0.85)' : 'rgba(255, 220, 0, 0.55)';
      s.style.outline = i === localIdx ? '2px solid #ff9900' : 'none';
    });
    if (spans[localIdx]) spans[localIdx].scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const goToGlobalHighlight = useCallback((delta) => {
    const hits = allHitsRef.current;
    if (!hits.length) return;
    const total = hits.length;
    const newGlobal = globalHighlightIndex < 0
      ? (delta > 0 ? 0 : total - 1)
      : (globalHighlightIndex + delta + total) % total;
    const { page, localIndex } = hits[newGlobal];
    setGlobalHighlightIndex(newGlobal);
    if (page === currentPage) {
      activateLocalSpan(localIndex);
    } else {
      pendingLocalHighlightRef.current = localIndex;
      setCurrentPage(page);
    }
  }, [globalHighlightIndex, currentPage]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: '#2b2b2b',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          background: '#1a1a1a',
          borderBottom: '1px solid #444',
          flexShrink: 0,
        }}
      >
        {/* Editable search / highlight field */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0, maxWidth: 340 }}>
          <i className="bi bi-search" style={{ color: '#ffe066', fontSize: '0.75rem', flexShrink: 0 }} />
          <input
            type="text"
            value={customSearch}
            onChange={(e) => setCustomSearch(e.target.value)}
            placeholder="Search / highlight text…"
            style={{
              flex: 1,
              minWidth: 0,
              background: '#333',
              border: '1px solid rgba(255,220,0,0.5)',
              borderRadius: 4,
              color: '#ffe066',
              fontSize: '0.72rem',
              padding: '2px 6px',
              outline: 'none',
            }}
          />
          {customSearch !== defaultSearch && (
            <button
              style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: '0 2px', fontSize: '0.75rem' }}
              onClick={() => setCustomSearch(defaultSearch)}
              title="Reset to suggested search"
            >↺</button>
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            className="btn btn-sm btn-outline-secondary"
            style={{ color: '#ccc', borderColor: '#555' }}
            onClick={() => setScale((s) => Math.max(0.5, +(s - 0.2).toFixed(1)))}
            title="Zoom out"
          >−</button>
          <span style={{ color: '#aaa', fontSize: '0.75rem', minWidth: 36, textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </span>
          <button
            className="btn btn-sm btn-outline-secondary"
            style={{ color: '#ccc', borderColor: '#555' }}
            onClick={() => setScale((s) => Math.min(3, +(s + 0.2).toFixed(1)))}
            title="Zoom in"
          >+</button>
          <div style={{ width: 1, background: '#555', height: 20, margin: '0 4px' }} />
          <button
            className="btn btn-sm btn-outline-secondary"
            style={{ color: '#ccc', borderColor: '#555' }}
            onClick={() => goTo(currentPage - 1)}
            disabled={currentPage <= 1}
          >‹</button>
          <input
            type="number"
            min={1}
            max={numPages}
            value={currentPage}
            onChange={(e) => goTo(Number(e.target.value))}
            style={{
              width: 48,
              textAlign: 'center',
              background: '#333',
              border: '1px solid #555',
              color: '#eee',
              borderRadius: 4,
              fontSize: '0.78rem',
              padding: '1px 4px',
            }}
          />
          <span style={{ color: '#888', fontSize: '0.75rem' }}>/ {numPages}</span>
          <button
            className="btn btn-sm btn-outline-secondary"
            style={{ color: '#ccc', borderColor: '#555' }}
            onClick={() => goTo(currentPage + 1)}
            disabled={currentPage >= numPages}
          >›</button>
          {matchPage && (
            <button
              className="btn btn-sm"
              style={{
                background: currentPage === matchPage ? 'rgba(255,220,0,0.08)' : 'rgba(255,220,0,0.2)',
                color: currentPage === matchPage ? 'rgba(255,224,102,0.5)' : '#ffe066',
                border: '1px solid rgba(255,220,0,0.5)',
                fontSize: '0.72rem',
                cursor: currentPage === matchPage ? 'default' : 'pointer',
              }}
              onClick={() => goTo(matchPage)}
              disabled={currentPage === matchPage}
              title={currentPage === matchPage ? 'You are on the best match page' : 'Jump to best match page'}
            >
              p.{matchPage} ★
            </button>
          )}
          {tokens.length > 0 && (
            <>
              <div style={{ width: 1, background: '#555', height: 20, margin: '0 2px' }} />
              <button
                className="btn btn-sm"
                style={{ background: 'none', border: '1px solid #555', color: totalHighlights > 0 ? '#ccc' : '#555', fontSize: '0.72rem', padding: '1px 6px' }}
                onClick={() => goToGlobalHighlight(-1)}
                disabled={totalHighlights === 0}
                title="Previous highlight"
              >⬆</button>
              <span style={{ color: totalHighlights > 0 ? '#888' : '#555', fontSize: '0.72rem', minWidth: 40, textAlign: 'center' }}>
                {totalHighlights > 0 ? (globalHighlightIndex >= 0 ? `${globalHighlightIndex + 1}/` : '') + totalHighlights : '0'}
              </span>
              <button
                className="btn btn-sm"
                style={{ background: 'none', border: '1px solid #555', color: totalHighlights > 0 ? '#ccc' : '#555', fontSize: '0.72rem', padding: '1px 6px' }}
                onClick={() => goToGlobalHighlight(1)}
                disabled={totalHighlights === 0}
                title="Next highlight"
              >⬇</button>
            </>
          )}
        </div>
      </div>

      {/* Canvas area */}
      <div ref={scrollContainerRef} style={{ flex: 1, overflowY: 'auto', display: 'flex', justifyContent: 'center', padding: 12 }}>
        {loading && (
          <div style={{ color: '#aaa', marginTop: 40, textAlign: 'center' }}>
            <div className="spinner-border text-light mb-2" />
            <div style={{ fontSize: '0.85rem' }}>Loading PDF{tokens.length > 0 ? ' and searching…' : '…'}</div>
          </div>
        )}
        {error && (
          <div style={{ color: '#f88', marginTop: 40, fontSize: '0.85rem' }}>{error}</div>
        )}
        {!loading && !error && (
          <div style={{ position: 'relative', display: 'inline-block', boxShadow: '0 2px 16px #000a' }}>
            <canvas ref={canvasRef} style={{ display: 'block' }} />
            <div
              ref={textLayerRef}
              style={{ position: 'absolute', top: 0, left: 0, overflow: 'hidden', pointerEvents: 'none' }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
